import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { BookOpen, CheckSquare, Calendar, Clock, LogOut, Menu, User, ChevronRight, XCircle, Video, Globe, BarChart3, Paperclip, Trash2, Plus, ExternalLink, AlertTriangle, Edit2, PlusCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import TutorAvailability from "./TutorAvailability";
import TutorSettings from "./TutorSettings";
import TutorQuizResults from "./TutorQuizResults";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { getTimezoneAbbr, utcTimeToLocal, utcTimeToLocalInput, localTimeToUtc, formatTime12WithTz } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const tutorTzAbbr = getTimezoneAbbr();

// formatTime: converts a UTC "HH:MM" string to the tutor's local time display.
// Optionally pass the session date for DST-accurate conversion.
const formatTime = (utcTime, dateStr) => utcTimeToLocal(utcTime, dateStr);

// ==================== TUTOR SCHEDULE (Auto-generated from bookings) ====================
const TutorSchedule = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [bookModal, setBookModal] = useState(null); // { enrollment_id, student_name }
  const [bookForm, setBookForm] = useState({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" });
  const [booking, setBooking] = useState(false);

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/tutor/my-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(res.data);
    } catch (error) {
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStudents(); }, []);

  const cancelEnrollment = async () => {
    if (!cancelTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/enrollments/${cancelTarget}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Enrollment cancelled");
      setCancelTarget(null);
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const bookForStudent = async () => {
    if (!bookModal || !bookForm.scheduled_date || !bookForm.scheduled_time) return toast.error("Please fill all fields");
    setBooking(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/tutor/book-for-student`, {
        enrollment_id: bookModal.enrollment_id,
        session_type: bookForm.session_type,
        week_number: parseInt(bookForm.week_number),
        scheduled_date: bookForm.scheduled_date,
        scheduled_time: localTimeToUtc(bookForm.scheduled_time, bookForm.scheduled_date)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session booked!");
      setBookModal(null);
      setBookForm({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to book session");
    } finally {
      setBooking(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  // Group students by day
  const byDay = {};
  students.forEach(s => {
    const day = s.scheduled_day ?? -1;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(s);
  });

  // Sort days
  const sortedDays = Object.keys(byDay).map(Number).sort();

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h1 className="text-3xl font-bold" data-testid="schedule-title">My Schedule</h1>
        <span className="flex items-center gap-1.5 text-xs font-medium text-[#4A4A4A] bg-[#F0EFEB] border border-[#0A0A0A]/10 px-3 py-1 rounded-full">
          <Globe className="w-3.5 h-3.5" /> All times in {tutorTzAbbr || "your local timezone"}
        </span>
      </div>
      <p className="text-[#4A4A4A] mb-8">Your upcoming classes based on student bookings</p>

      {students.length === 0 ? (
        <div className="brutalist-card p-12 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
          <p className="text-[#4A4A4A] mb-2">No students have booked classes yet.</p>
          <p className="text-sm text-[#4A4A4A]">Make sure you've set your availability and are assigned to languages.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDays.map(day => (
            <div key={day} className="brutalist-card p-6" data-testid={`schedule-day-${day}`}>
              <h2 className="font-bold text-xl mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {day >= 0 ? DAY_NAMES[day] : "Unscheduled"}
              </h2>
              <div className="space-y-3">
                {byDay[day].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")).map(s => (
                  <div key={s.enrollment_id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-[#F0EFEB] rounded-lg border-2 border-[#0A0A0A]/10" data-testid={`schedule-student-${s.enrollment_id}`}>
                    <div className="w-10 h-10 rounded-full bg-[#A0D2F8] border-2 border-[#0A0A0A] flex items-center justify-center font-bold text-sm">
                      {s.student_name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">{s.student_name}</div>
                      <div className="text-sm text-[#4A4A4A]">
                        {s.language_flag} {s.course_name} &middot; Week {s.current_week}/{s.duration_weeks}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">{formatTime12WithTz(s.scheduled_time, tutorTzAbbr)}</div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => { setBookModal({ enrollment_id: s.enrollment_id, student_name: s.student_name, duration_weeks: s.duration_weeks }); setBookForm({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" }); }}
                        className="p-2 text-[#3C8DE4] hover:bg-[#D6E6FA] rounded-lg"
                        title="Book a session for this student"
                        data-testid={`book-for-student-${s.enrollment_id}`}
                      >
                        <PlusCircle className="w-5 h-5" />
                      </button>
                      <Link
                        to={`/tutor/attendance/${s.enrollment_id}`}
                        className="p-2 hover:bg-[#D6E6FA] rounded-lg"
                        data-testid={`go-attendance-${s.enrollment_id}`}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => setCancelTarget(s.enrollment_id)}
                        className="p-2 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded-lg"
                        title="Cancel entire enrollment"
                        data-testid={`cancel-student-${s.enrollment_id}`}
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel This Student's Lesson?"
        description="This will cancel the enrollment and remove all scheduled sessions for this student."
        onConfirm={cancelEnrollment}
        destructive
      />

      {/* Book Session for Student Modal */}
      {bookModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-6 w-full max-w-md" data-testid="book-session-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Book Session for {bookModal.student_name}</h3>
              <button onClick={() => setBookModal(null)} className="p-1 hover:bg-[#F0EFEB] rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Session Type</label>
                <select
                  value={bookForm.session_type}
                  onChange={e => {
                    const type = e.target.value;
                    setBookForm({ ...bookForm, session_type: type, week_number: type === "final_exam" ? 9 : bookForm.week_number });
                  }}
                  className="brutalist-input w-full"
                  data-testid="book-session-type"
                >
                  <option value="makeup">Make-up</option>
                  <option value="reschedule">Reschedule</option>
                  <option value="final_exam">Final Exam</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Week (Curriculum)</label>
                {bookForm.session_type === "final_exam" ? (
                  <div className="brutalist-input w-full bg-[#F0EFEB] text-[#4A4A4A] cursor-not-allowed flex items-center gap-2" data-testid="book-session-week">
                    Week 9 — Final Exam <span className="text-xs ml-auto opacity-60">locked</span>
                  </div>
                ) : (
                  <select
                    value={bookForm.week_number}
                    onChange={e => setBookForm({ ...bookForm, week_number: e.target.value })}
                    className="brutalist-input w-full"
                    data-testid="book-session-week"
                  >
                    {Array.from({ length: bookModal.duration_weeks || 8 }, (_, i) => i + 1).map(w => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Date</label>
                <input
                  type="date"
                  value={bookForm.scheduled_date}
                  onChange={e => setBookForm({ ...bookForm, scheduled_date: e.target.value })}
                  className="brutalist-input w-full"
                  data-testid="book-session-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Time <span className="normal-case font-normal text-[#4A4A4A]">({tutorTzAbbr || "your local time"})</span></label>
                <input
                  type="time"
                  value={bookForm.scheduled_time}
                  onChange={e => setBookForm({ ...bookForm, scheduled_time: e.target.value })}
                  className="brutalist-input w-full"
                  data-testid="book-session-time"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setBookModal(null)} className="flex-1 py-2 border-2 border-[#0A0A0A] rounded-lg font-medium hover:bg-[#F0EFEB] transition-colors">Cancel</button>
                <button
                  onClick={bookForStudent}
                  disabled={booking}
                  className="flex-1 brutalist-btn-primary py-2"
                  data-testid="confirm-book-session"
                >
                  {booking ? "Booking..." : "Book Session"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ATTENDANCE PAGE (Per enrollment) ====================
const AttendancePage = () => {
  const location = useLocation();
  const [enrollment, setEnrollment] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelSessionTarget, setCancelSessionTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [addSessionModal, setAddSessionModal] = useState(false);
  const [addForm, setAddForm] = useState({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" });
  const [adding, setAdding] = useState(false);
  const [editSession, setEditSession] = useState(null); // { id, scheduled_date, scheduled_time }
  const [editing, setEditing] = useState(false);
  const [changeTimeModal, setChangeTimeModal] = useState(false);
  const [changeTimeForm, setChangeTimeForm] = useState({ new_time: "14:00", new_day: 0, apply_to: "all", weeks: [] });
  const [changingTime, setChangingTime] = useState(false);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];
    if (id && id !== 'attendance') loadData(id);
    else setLoading(false);
  }, [location.pathname]);

  const loadData = async (enrollmentId) => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const sessionsRes = await axios.get(`${API}/class-sessions/enrollment/${enrollmentId}`, { headers });
      setSessions(sessionsRes.data);
      
      const studentsRes = await axios.get(`${API}/tutor/my-students`, { headers });
      const found = studentsRes.data.find(s => s.enrollment_id === enrollmentId);
      setEnrollment(found);
    } catch (error) {
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const reload = () => enrollment && loadData(enrollment.enrollment_id);

  const markAttendance = async (sessionId, attended, feedback) => {
    if (!enrollment) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/attendance`, {
        enrollment_id: enrollment.enrollment_id,
        class_session_id: sessionId,
        attended,
        feedback: feedback || ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(attended ? "Marked present!" : "Marked absent");
      const sessionsRes = await axios.get(`${API}/class-sessions/enrollment/${enrollment.enrollment_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const clearAttendance = async (sessionId) => {
    if (!enrollment) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/attendance/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Attendance cleared");
      const sessionsRes = await axios.get(`${API}/class-sessions/enrollment/${enrollment.enrollment_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear attendance");
    }
  };

  const cancelSession = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/sessions/${cancelSessionTarget}/cancel`, { reason: cancelReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Session cancelled — replacement added to schedule");
      setCancelSessionTarget(null);
      setCancelReason("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel session");
    } finally {
      setCancelling(false);
    }
  };

  const addOneOffSession = async () => {
    if (!addForm.scheduled_date || !addForm.scheduled_time) return toast.error("Please fill all fields");
    setAdding(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/tutor/add-session`, {
        enrollment_id: enrollment.enrollment_id,
        session_type: addForm.session_type,
        week_number: parseInt(addForm.week_number),
        scheduled_date: addForm.scheduled_date,
        scheduled_time: localTimeToUtc(addForm.scheduled_time, addForm.scheduled_date)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session added!");
      setAddSessionModal(false);
      setAddForm({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add session");
    } finally {
      setAdding(false);
    }
  };

  const saveSessionDateTime = async () => {
    if (!editSession) return;
    setEditing(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.put(`${API}/sessions/${editSession.id}/datetime`, {
        scheduled_date: editSession.scheduled_date,
        scheduled_time: localTimeToUtc(editSession.scheduled_time, editSession.scheduled_date)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session confirmed & schedule re-sorted!");
      setEditSession(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update session");
    } finally {
      setEditing(false);
    }
  };

  const dismissReplacement = async (sessionId) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/sessions/${sessionId}/dismiss`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Replacement suggestion dismissed");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dismiss");
    }
  };

  const changeClassTime = async () => {
    if (!changeTimeForm.new_time) return toast.error("Please enter a time");
    if (changeTimeForm.apply_to === "specific" && changeTimeForm.weeks.length === 0)
      return toast.error("Select at least one week");
    setChangingTime(true);
    try {
      const token = localStorage.getItem("access_token");

      // Use the real upcoming class date (shifted onto the new day-of-week) as the
      // DST-accurate reference for the local-to-UTC conversion, instead of today's date.
      const today = new Date().toISOString().slice(0, 10);
      const relevantSessions = sessions.filter(s =>
        !["cancelled", "missed", "rescheduled"].includes(s.status) &&
        s.scheduled_date >= today &&
        (changeTimeForm.apply_to === "all" || changeTimeForm.weeks.map(Number).includes(s.week_number))
      ).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

      let refDate = relevantSessions[0]?.scheduled_date || today;
      const refDateObj = new Date(`${refDate}T00:00:00`);
      const currentIsoDay = (refDateObj.getDay() + 6) % 7; // JS Sun=0..Sat=6 -> Mon=0..Sun=6
      refDateObj.setDate(refDateObj.getDate() + (changeTimeForm.new_day - currentIsoDay));
      refDate = refDateObj.toISOString().slice(0, 10);

      const utcTime = localTimeToUtc(changeTimeForm.new_time, refDate);
      const res = await axios.put(
        `${API}/tutor/enrollment/${enrollment.enrollment_id}/change-time`,
        { new_time: utcTime, new_day: changeTimeForm.new_day, apply_to: changeTimeForm.apply_to, weeks: changeTimeForm.weeks.map(Number) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${res.data.updated_count} session(s)! Students notified via email.`);
      setChangeTimeModal(false);
      setChangeTimeForm({ new_time: "14:00", new_day: 0, apply_to: "all", weeks: [] });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change class time");
    } finally {
      setChangingTime(false);
    }
  };

  // Upcoming active week numbers for the "specific weeks" picker
  const upcomingWeeks = [...new Set(
    sessions
      .filter(s => !["cancelled", "missed", "rescheduled"].includes(s.status) && !s.is_suggestion)
      .map(s => s.week_number)
  )].sort((a, b) => a - b);

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  if (!enrollment) {
    return (
      <div>
        <h1 className="text-3xl font-bold mb-2" data-testid="attendance-title">Attendance</h1>
        <p className="text-[#4A4A4A] mb-4">Select a student from your schedule to mark attendance.</p>
        <Link to="/tutor/schedule" className="brutalist-btn-primary inline-flex items-center gap-2">
          View Schedule <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link to="/tutor/schedule" className="text-[#4A4A4A] hover:text-[#0A0A0A] text-sm mb-4 inline-block">&larr; Back to Schedule</Link>
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#A0D2F8] border-2 border-[#0A0A0A] flex items-center justify-center text-xl font-bold">
            {enrollment.student_name?.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="attendance-student-name">{enrollment.student_name}</h1>
            <p className="text-[#4A4A4A]">
              {enrollment.language_flag} {enrollment.course_name} &middot; {enrollment.scheduled_day_name}s at {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const upcoming = sessions
                  .filter(s => !["cancelled", "missed", "rescheduled"].includes(s.status) && s.scheduled_date >= today)
                  .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];
                return utcTimeToLocal(enrollment.scheduled_time, upcoming?.scheduled_date);
              })()}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => {
              setChangeTimeModal(true);
              setChangeTimeForm({ new_time: utcTimeToLocalInput(enrollment.scheduled_time), new_day: enrollment.scheduled_day ?? 0, apply_to: "all", weeks: [] });
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#0A0A0A] bg-white hover:bg-[#F0EFEB] text-sm font-bold transition-colors"
            data-testid="change-class-time-btn"
          >
            <Clock className="w-3.5 h-3.5" /> Change Day & Time
          </button>
          <button
            onClick={() => { setAddSessionModal(true); setAddForm({ session_type: "makeup", week_number: 1, scheduled_date: "", scheduled_time: "14:00" }); }}
            className="brutalist-btn-primary text-sm flex items-center gap-2"
            data-testid="add-session-btn"
          >
            <Plus className="w-4 h-4" /> Add Session
          </button>
        </div>
      </div>

      {/* Session count indicator */}
      {(() => {
        const activeCount = sessions.filter(s => s.status !== "cancelled").length;
        const targetCount = enrollment.duration_weeks || 8;
        return activeCount < targetCount ? (
          <div className="flex items-center gap-2 p-3 mb-4 bg-[#FFF3CD] border-2 border-[#FFC107] rounded-xl text-sm" data-testid="session-count-warning">
            <AlertTriangle className="w-4 h-4 text-[#856404] flex-shrink-0" />
            <span className="text-[#856404]">{activeCount}/{targetCount} active sessions scheduled</span>
          </div>
        ) : null;
      })()}

      <div className="space-y-3">
        {sessions.map(session => (
          <SessionRow
            key={session.id}
            session={session}
            onMark={(attended, feedback) => markAttendance(session.id, attended, feedback)}
            onClear={() => clearAttendance(session.id)}
            onCancel={() => { setCancelSessionTarget(session.id); setCancelReason(""); }}
            onEdit={() => setEditSession({ id: session.id, scheduled_date: session.scheduled_date, scheduled_time: utcTimeToLocalInput(session.scheduled_time, session.scheduled_date) })}
            onDismiss={() => dismissReplacement(session.id)}
          />
        ))}
      </div>

      {/* Cancel Session Modal */}
      {cancelSessionTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-6 w-full max-w-md" data-testid="cancel-session-modal">
            <h3 className="font-bold text-lg mb-3">Cancel This Session?</h3>
            <p className="text-sm text-[#4A4A4A] mb-4">The student will be notified by email and a make-up session will be automatically added to the end of their schedule.</p>
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Reason (optional)</label>
              <input
                type="text"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g. Tutor unavailable, holiday..."
                className="brutalist-input w-full"
                data-testid="cancel-session-reason"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setCancelSessionTarget(null); setCancelReason(""); }} className="flex-1 py-2 border-2 border-[#0A0A0A] rounded-lg font-medium hover:bg-[#F0EFEB] transition-colors">Keep Session</button>
              <button
                onClick={cancelSession}
                disabled={cancelling}
                className="flex-1 py-2 bg-[#FF6B6B] border-2 border-[#0A0A0A] rounded-lg font-bold text-white hover:bg-[#FF4444] transition-colors"
                data-testid="confirm-cancel-session"
              >
                {cancelling ? "Cancelling..." : "Cancel Session"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add One-Off Session Modal */}
      {addSessionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-6 w-full max-w-md" data-testid="add-session-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Add One-Off Session</h3>
              <button onClick={() => setAddSessionModal(false)} className="p-1 hover:bg-[#F0EFEB] rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Session Type</label>
                <select value={addForm.session_type} onChange={e => {
                  const type = e.target.value;
                  setAddForm({ ...addForm, session_type: type, week_number: type === "final_exam" ? 9 : addForm.week_number });
                }} className="brutalist-input w-full" data-testid="add-session-type">
                  <option value="makeup">Make-up</option>
                  <option value="reschedule">Reschedule</option>
                  <option value="final_exam">Final Exam</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Week (Curriculum)</label>
                {addForm.session_type === "final_exam" ? (
                  <div className="brutalist-input w-full bg-[#F0EFEB] text-[#4A4A4A] cursor-not-allowed flex items-center gap-2" data-testid="add-session-week">
                    Week 9 — Final Exam <span className="text-xs ml-auto opacity-60">locked</span>
                  </div>
                ) : (
                  <select value={addForm.week_number} onChange={e => setAddForm({ ...addForm, week_number: e.target.value })} className="brutalist-input w-full" data-testid="add-session-week">
                    {Array.from({ length: enrollment.duration_weeks || 8 }, (_, i) => i + 1).map(w => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Date</label>
                <input type="date" value={addForm.scheduled_date} onChange={e => setAddForm({ ...addForm, scheduled_date: e.target.value })} className="brutalist-input w-full" data-testid="add-session-date" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">Time <span className="normal-case font-normal text-[#4A4A4A]">({tutorTzAbbr || "your local time"})</span></label>
                <input type="time" value={addForm.scheduled_time} onChange={e => setAddForm({ ...addForm, scheduled_time: e.target.value })} className="brutalist-input w-full" data-testid="add-session-time" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setAddSessionModal(false)} className="flex-1 py-2 border-2 border-[#0A0A0A] rounded-lg font-medium hover:bg-[#F0EFEB] transition-colors">Cancel</button>
                <button onClick={addOneOffSession} disabled={adding} className="flex-1 brutalist-btn-primary py-2" data-testid="confirm-add-session">
                  {adding ? "Adding..." : "Add Session"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Date/Time Modal */}
      {editSession && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-6 w-full max-w-md" data-testid="edit-session-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Session Date/Time</h3>
              <button onClick={() => setEditSession(null)} className="p-1 hover:bg-[#F0EFEB] rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">New Date</label>
                <input type="date" value={editSession.scheduled_date} onChange={e => setEditSession({ ...editSession, scheduled_date: e.target.value })} className="brutalist-input w-full" data-testid="edit-session-date" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">New Time <span className="normal-case font-normal text-[#4A4A4A]">({tutorTzAbbr || "your local time"})</span></label>
                <input type="time" value={editSession.scheduled_time} onChange={e => setEditSession({ ...editSession, scheduled_time: e.target.value })} className="brutalist-input w-full" data-testid="edit-session-time" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditSession(null)} className="flex-1 py-2 border-2 border-[#0A0A0A] rounded-lg font-medium hover:bg-[#F0EFEB] transition-colors">Cancel</button>
                <button onClick={saveSessionDateTime} disabled={editing} className="flex-1 brutalist-btn-primary py-2" data-testid="save-session-datetime">
                  {editing ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Change Class Time Modal */}
      {changeTimeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-6 w-full max-w-md" data-testid="change-time-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#3C8DE4]" /> Change Class Day & Time
              </h3>
              <button onClick={() => setChangeTimeModal(false)} className="p-1 hover:bg-[#F0EFEB] rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {/* Day of week picker */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-2">Day of Week</label>
                <div className="grid grid-cols-7 gap-1">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setChangeTimeForm(prev => ({ ...prev, new_day: i }))}
                      className={`py-1.5 rounded-lg border-2 text-xs font-bold transition-colors ${changeTimeForm.new_day === i ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]/30 hover:bg-[#F0EFEB]"}`}
                      data-testid={`day-picker-${i}`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-1">
                  New Time <span className="normal-case font-normal">({tutorTzAbbr || "your local time"})</span>
                </label>
                <input type="time" value={changeTimeForm.new_time} onChange={e => setChangeTimeForm({ ...changeTimeForm, new_time: e.target.value })} className="brutalist-input w-full" data-testid="change-time-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-2">Apply To</label>
                <div className="space-y-2">
                  {[
                    { value: "all", label: "All Upcoming Sessions", sub: "Updates every future class in this student's schedule" },
                    { value: "specific", label: "Specific Weeks", sub: "Choose individual weeks to update" },
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${changeTimeForm.apply_to === opt.value ? "border-[#3C8DE4] bg-[#D6E6FA]/30" : "border-[#0A0A0A]/20 hover:bg-[#F0EFEB]"}`}>
                      <input type="radio" name="apply_to" value={opt.value} checked={changeTimeForm.apply_to === opt.value} onChange={() => setChangeTimeForm({ ...changeTimeForm, apply_to: opt.value, weeks: [] })} className="mt-0.5" data-testid={`apply-to-${opt.value}`} />
                      <div>
                        <div className="font-bold text-sm">{opt.label}</div>
                        <div className="text-xs text-[#4A4A4A]">{opt.sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {changeTimeForm.apply_to === "specific" && (
                <div data-testid="week-picker">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] block mb-2">Select Weeks</label>
                  {upcomingWeeks.length === 0 ? (
                    <p className="text-sm text-[#4A4A4A] italic">No upcoming sessions found.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {upcomingWeeks.map(w => {
                        const selected = changeTimeForm.weeks.includes(w);
                        return (
                          <button key={w} type="button"
                            onClick={() => setChangeTimeForm(prev => ({ ...prev, weeks: selected ? prev.weeks.filter(x => x !== w) : [...prev.weeks, w] }))}
                            className={`px-3 py-1.5 rounded-lg border-2 text-sm font-bold transition-colors ${selected ? "bg-[#3C8DE4] text-white border-[#3C8DE4]" : "bg-white border-[#0A0A0A] hover:bg-[#F0EFEB]"}`}
                            data-testid={`week-toggle-${w}`}
                          >Week {w}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-[#4A4A4A] bg-[#F0EFEB] px-3 py-2 rounded-lg">
                The student will receive an automated email notification about this change.
              </p>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setChangeTimeModal(false)} className="flex-1 py-2 border-2 border-[#0A0A0A] rounded-lg font-medium hover:bg-[#F0EFEB] transition-colors">Cancel</button>
                <button onClick={changeClassTime} disabled={changingTime} className="flex-1 brutalist-btn-primary py-2" data-testid="confirm-change-time">
                  {changingTime ? "Saving..." : "Apply Change"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SessionRow = ({ session, onMark, onClear, onCancel, onEdit, onDismiss }) => {
  const [feedback, setFeedback] = useState(session.feedback || "");
  const [showFeedback, setShowFeedback] = useState(session.attended === true);
  const [resources, setResources] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const [newRes, setNewRes] = useState({ title: "", type: "link", url: "" });
  const [addingRes, setAddingRes] = useState(false);

  useEffect(() => {
    setShowFeedback(session.attended === true);
    if (session.attended === true) loadResources();
  }, [session.attended]);

  const loadResources = async () => {
    setLoadingRes(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/session-resources/session/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(res.data);
    } catch {} finally { setLoadingRes(false); }
  };

  const addResource = async () => {
    if (!newRes.title.trim() || !newRes.url.trim()) return toast.error("Title and URL are required");
    setAddingRes(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/session-resources`, {
        session_id: session.id,
        enrollment_id: session.enrollment_id,
        title: newRes.title,
        type: newRes.type,
        url: newRes.url
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Resource added!");
      setNewRes({ title: "", type: "link", url: "" });
      loadResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add resource");
    } finally { setAddingRes(false); }
  };

  const deleteResource = async (resourceId) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/session-resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resource removed");
      loadResources();
    } catch { toast.error("Failed to remove resource"); }
  };

  const TYPE_ICON = { link: "🔗", video: "🎬", pdf: "📄" };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    } catch { return dateStr; }
  };

  const SESSION_TYPE_LABELS = { regular: null, makeup: "Make-up", reschedule: "Reschedule", final_exam: "Final Exam" };
  const STATUS_STYLES = {
    cancelled: "opacity-60 border-[#FF6B6B]",
    missed: "opacity-70 border-[#FF9800]",
    rescheduled: "opacity-70 border-[#9C27B0]",
    replacement: "border-[#FFC107]",
    scheduled: ""
  };

  const isCancelled = session.status === "cancelled";
  const isMissed = session.status === "missed";
  const isRescheduled = session.status === "rescheduled";
  const isTerminal = isCancelled || isMissed || isRescheduled;
  const isReplacement = session.is_replacement || session.status === "replacement";
  const isSuggestion = session.is_suggestion && isReplacement && !isCancelled;

  return (
    <div className={`brutalist-card p-4 ${STATUS_STYLES[session.status] || ""} ${
      session.attended === true ? 'bg-[#D6E6FA]/20' :
      session.attended === false ? 'bg-[#FFAEBC]/20' : ''
    }`} data-testid={`session-week-${session.week_number}`}>
      {/* Suggestion Banner for unconfirmed replacements */}
      {isSuggestion && (
        <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 bg-[#FFF3CD] border border-[#FFC107] rounded-xl">
          <div className="flex items-center gap-2 text-xs text-[#856404] font-medium">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Make-up suggestion — set a confirmed date or dismiss
          </div>
          <button
            onClick={onDismiss}
            className="text-xs font-bold text-[#856404] border border-[#FFC107] px-2 py-0.5 rounded-lg hover:bg-[#FFC107]/20 transition-colors whitespace-nowrap flex-shrink-0"
            data-testid={`dismiss-replacement-${session.id}`}
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-16 text-center">
          <div className="text-xs text-[#4A4A4A] uppercase font-bold">Week</div>
          <div className="text-2xl font-bold">{session.week_number}</div>
          {session.session_type && session.session_type !== "regular" && (
            <div className="text-xs px-1 py-0.5 bg-[#FFF3CD] text-[#856404] rounded font-bold mt-0.5 leading-tight">
              {SESSION_TYPE_LABELS[session.session_type] || session.session_type}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className={`font-medium ${isTerminal ? "line-through text-[#4A4A4A]" : ""}`}>{formatDate(session.scheduled_date)}</div>
          <div className="text-sm text-[#4A4A4A]">{formatTime(session.scheduled_time, session.scheduled_date)}</div>
          {isCancelled && session.cancel_reason && (
            <div className="text-xs text-[#FF6B6B] mt-0.5">Reason: {session.cancel_reason}</div>
          )}
          {isCancelled && (
            <div className="text-xs font-bold text-[#FF6B6B] mt-0.5">CANCELLED</div>
          )}
          {isMissed && (
            <div className="text-xs font-bold text-[#FF9800] mt-0.5">MISSED — make-up added</div>
          )}
          {isRescheduled && (
            <div className="text-xs font-bold text-[#9C27B0] mt-0.5">RESCHEDULED — replacement added</div>
          )}
          {isReplacement && !isCancelled && !isSuggestion && (
            <div className="text-xs font-bold text-[#856404] mt-0.5">REPLACEMENT</div>
          )}
        </div>
        {!isTerminal && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => {
                if (session.attended === true) {
                  // Toggle off — return to neutral
                  onClear();
                  setShowFeedback(false);
                } else {
                  onMark(true, feedback);
                  setShowFeedback(true);
                }
              }}
              className={`px-4 py-2 rounded-full border-2 border-[#0A0A0A] text-sm font-bold transition-colors ${
                session.attended === true ? 'bg-[#D6E6FA] ring-2 ring-[#3C8DE4]' : 'bg-white hover:bg-[#D6E6FA]/50'
              }`}
              data-testid={`mark-present-${session.week_number}`}
              title={session.attended === true ? "Click to unmark" : "Mark as present"}
            >
              {session.attended === true ? "✓ Present" : "Present"}
            </button>
            <button
              onClick={() => {
                if (session.attended === false) {
                  // Toggle off — return to neutral
                  onClear();
                  setShowFeedback(false);
                } else {
                  onMark(false, "");
                  setShowFeedback(false);
                }
              }}
              className={`px-4 py-2 rounded-full border-2 border-[#0A0A0A] text-sm font-bold transition-colors ${
                session.attended === false ? 'bg-[#FFAEBC] ring-2 ring-[#FF6B6B]' : 'bg-white hover:bg-[#FFAEBC]/50'
              }`}
              data-testid={`mark-absent-${session.week_number}`}
              title={session.attended === false ? "Click to unmark" : "Mark as absent"}
            >
              {session.attended === false ? "✗ Absent" : "Absent"}
            </button>
          </div>
        )}
        <div className="flex gap-1 flex-wrap">
          {!isTerminal && (
            <button onClick={onEdit} className="p-1.5 text-[#3C8DE4] hover:bg-[#D6E6FA] rounded" title="Edit date & time" data-testid={`edit-session-${session.id}`}>
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {!isTerminal && !session.attendance_marked && !isSuggestion && (
            <button onClick={onCancel} className="p-1.5 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded" title="Cancel this session" data-testid={`cancel-session-${session.id}`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {showFeedback && (
        <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10">
          <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Feedback (unlocks assignments)</label>
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Great work this week! Keep practicing..."
              className="brutalist-input flex-1"
              data-testid={`feedback-${session.week_number}`}
            />
            <button
              onClick={() => onMark(true, feedback)}
              className="brutalist-btn-primary text-sm px-4"
              data-testid={`save-feedback-${session.week_number}`}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10">
          <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" /> Extra Resources for This Session
            {resources.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-[#3C8DE4] text-white text-xs rounded-full font-bold">{resources.length}</span>
            )}
          </label>

          {loadingRes ? (
            <p className="text-xs text-[#4A4A4A] mt-2">Loading resources...</p>
          ) : (
            <div className="mt-2 space-y-1.5">
              {resources.map(r => (
                <div key={r.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-[#0A0A0A]/10" data-testid={`resource-item-${r.id}`}>
                  <span className="text-base leading-none">{TYPE_ICON[r.type] || "🔗"}</span>
                  <span className="text-sm flex-1 font-medium truncate">{r.title}</span>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-[#3C8DE4] hover:bg-[#D6E6FA] rounded"
                    title="Open link"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => deleteResource(r.id)}
                    className="p-1 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded"
                    title="Remove resource"
                    data-testid={`delete-resource-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-2 space-y-1.5">
            <div className="flex gap-2">
              <input
                type="text"
                value={newRes.title}
                onChange={(e) => setNewRes({ ...newRes, title: e.target.value })}
                placeholder="Resource title"
                className="brutalist-input text-sm flex-1"
                data-testid={`new-resource-title-${session.week_number}`}
              />
              <select
                value={newRes.type}
                onChange={(e) => setNewRes({ ...newRes, type: e.target.value })}
                className="brutalist-input text-sm w-24"
                data-testid={`new-resource-type-${session.week_number}`}
              >
                <option value="link">Link</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={newRes.url}
                onChange={(e) => setNewRes({ ...newRes, url: e.target.value })}
                placeholder="https://..."
                className="brutalist-input text-sm flex-1"
                data-testid={`new-resource-url-${session.week_number}`}
              />
              <button
                onClick={addResource}
                disabled={addingRes}
                className="brutalist-btn-primary text-sm px-3 flex items-center gap-1 whitespace-nowrap"
                data-testid={`add-resource-btn-${session.week_number}`}
              >
                <Plus className="w-3.5 h-3.5" /> {addingRes ? "..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TUTOR DASHBOARD LAYOUT ====================
const TutorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user && !user.profile_complete) {
      navigate("/tutor/setup");
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { path: "/tutor/schedule", label: "Schedule", icon: Calendar },
    { path: "/tutor/availability", label: "Availability", icon: Clock },
    { path: "/tutor/attendance", label: "Attendance", icon: CheckSquare },
    { path: "/tutor/quiz-results", label: "Quiz Results", icon: BarChart3 },
    { path: "/tutor/settings", label: "Settings", icon: Video },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed md:sticky top-0 left-0 h-screen w-64 bg-white border-r-2 border-[#0A0A0A] z-50 transform transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            <img src="/vv-logo.png" alt="VV" width={24} height={24} className="rounded-full" style={{ objectFit: "cover" }} />
            <span className="font-bold">Tutor Portal</span>
          </div>

          <div className="mb-6 p-4 bg-[#F0EFEB] rounded-lg">
            {user?.photo_url ? (
              <img src={user.photo_url} alt="" className="w-16 h-16 rounded-full border-2 border-[#0A0A0A] mx-auto mb-2 object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] mx-auto mb-2 flex items-center justify-center text-2xl font-bold">
                {user?.name?.charAt(0)}
              </div>
            )}
            <p className="text-center font-bold">{user?.name}</p>
          </div>

          <nav className="space-y-2 flex-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                  location.pathname.startsWith(link.path) ? 'bg-[#D6E6FA] border-2 border-[#0A0A0A]' : 'hover:bg-[#F0EFEB]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
          </nav>

          <button onClick={handleLogout} className="flex items-center gap-2 text-[#FF6B6B] font-medium" data-testid="tutor-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-8">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden mb-4">
          <Menu className="w-6 h-6" />
        </button>
        
        <Routes>
          <Route path="/dashboard" element={<TutorSchedule />} />
          <Route path="/schedule" element={<TutorSchedule />} />
          <Route path="/availability" element={<TutorAvailability />} />
          <Route path="/attendance/*" element={<AttendancePage />} />
          <Route path="/quiz-results/*" element={<TutorQuizResults />} />
          <Route path="/settings" element={<TutorSettings />} />
          <Route path="*" element={<TutorSchedule />} />
        </Routes>
      </main>
    </div>
  );
};

export default TutorDashboard;
